/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useLanguage } from '@/providers/LanguageProvider';
import { formatDateForDateRangeDisplay } from '@/utils/formatedDates';

import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { useMostRecentPreset, usePresets } from './presets';
import { DateRange } from './types';

interface DateRangeDisplayProps {
  range: DateRange;
  rangeCompare?: DateRange;
  locale: string;
  selectedPreset?: string;
  timeRange?: { from: string; to: string };
  factName?: string | null;
  classNames?: {
    rangeTriggerClassName?: string;
    labelClassName?: string;
    rangeClassName?: string;
    rangeValueClassName?: string;
    triggerIconClassName?: string;
  };
}

export const DateRangeDisplay: React.FC<DateRangeDisplayProps> = ({
  range,
  rangeCompare,
  locale,
  selectedPreset,
  timeRange,
  factName,
  classNames,
}) => {
  const { messages } = useLanguage();
  const langData = messages?.PromptFilters;
  const datePresets = usePresets();
  const mostRecentPresets = useMostRecentPreset(factName);

  const allPresets = [...datePresets, ...mostRecentPresets];

  const preset = allPresets.find((p) => p.name === selectedPreset);

  const isMostRecentPreset = mostRecentPresets.some(
    (p) => p.name === selectedPreset
  );

  // Check which preset should show tooltip
  const shouldShowTooltip = (presetName?: string) => {
    return (
      presetName &&
      ['last5mins', 'lastHour', 'last24hours', 'last48hours'].includes(
        presetName
      )
    );
  };

  const getDateRangeInfo = () => {
    if (selectedPreset) {
      const preset = allPresets.find((p) => p.name === selectedPreset);

      // Handle trace-based presets
      if (isMostRecentPreset && preset) {
        return preset.label;
      }

      if (preset?.name === 'last5mins' && range.from && range.to) {
        return `${preset.label} `;
      }
      if (preset?.name === 'lastHour' && range.from && range.to) {
        return `${preset.label} `;
      }
      return preset ? preset.label : '';
    }

    if (range.to && range.from) {
      const diffTime = Math.abs(range.to.getTime() - range.from.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

      return `${diffDays} ${langData?.date_range.day}${
        diffDays !== 1 ? `${messages?.General?.s}` : ''
      }`;
    }

    return `${langData?.date_range.range_warning}`;
  };

  return (
    <div
      className={cn('flex flex-col items-start', classNames?.rangeClassName)}
    >
      {preset?.name === 'allTime' ? (
        <div className="text-form-input-text font-normal text-form-input-rest">
          {langData?.date_range.default_observation_window}
        </div>
      ) : shouldShowTooltip(preset?.name) ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`text-form-input-text font-normal text-form-input-rest ${classNames?.labelClassName}`}
              >
                {getDateRangeInfo()}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div>
                {range.from &&
                  formatDateForDateRangeDisplay(range.from, locale)}{' '}
                {timeRange?.from}
                {range.to && (
                  <>
                    {' - '}
                    {formatDateForDateRangeDisplay(range.to, locale)}{' '}
                    {timeRange?.to}
                  </>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div
          className={`text-form-input-text font-normal text-form-input-rest ${classNames?.labelClassName}`}
        >
          {getDateRangeInfo()}
        </div>
      )}
      {/* Only show date range if:
          - Not "allTime"
          - Not trace-based preset
          - Not time-based presets that show tooltip
      */}
      {preset?.name !== 'allTime' &&
        !isMostRecentPreset &&
        preset?.name !== 'last5mins' &&
        preset?.name !== 'lastHour' &&
        preset?.name !== 'last24hours' &&
        preset?.name !== 'last48hours' && (
          <div
            className={cn(
              !classNames?.rangeClassName && 'pt-1',
              'text-form-input-text text-form-input-disabled',
              classNames?.rangeClassName,
              classNames?.rangeValueClassName
            )}
          >
            {range.from && formatDateForDateRangeDisplay(range.from, locale)}
            {range.to &&
              ` - ${formatDateForDateRangeDisplay(range.to, locale)}`}
          </div>
        )}
      {preset?.name !== 'allTime' && !isMostRecentPreset && rangeCompare && (
        <div className="-mt-1 text-xs opacity-60">
          {langData?.date_range.vs}.{' '}
          {rangeCompare.from &&
            formatDateForDateRangeDisplay(rangeCompare.from, locale)}
          {rangeCompare.to &&
            ` - ${formatDateForDateRangeDisplay(rangeCompare.to, locale)}`}
        </div>
      )}
    </div>
  );
};
